/**
 * Navigation — scroll-aware header + smooth scroll
 */

export function initNavigation(): void {
  initSmoothScroll();
  initStickyHeader();
  initMobileNavToggle();
}

// ─── Smooth Scroll ────────────────────────────────────────────────────────────

function initSmoothScroll(): void {
  document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach(link => {
    link.addEventListener('click', e => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });
}

// ─── Scroll-aware header ──────────────────────────────────────────────────────
//
// Desktop  (≥ breakpoint-md): position:sticky — uses .is-scrolled to shrink
//          padding. Hysteresis: add at 80px, remove below 30px.
//
// Mobile   (< breakpoint-md): position:fixed — hides on scroll-down via
//          .nav-hidden (translateY(-100%)), reveals on scroll-up with easing.
//          Always visible when overscrolled past the top (scrollY ≤ 0).
//
// --nav-height CSS custom property is kept in sync for the mobile
// #main-content padding-top offset.

function initStickyHeader(): void {
  const header = document.querySelector<HTMLElement>('.site-header');
  if (!header) return;

  // Keep --nav-height in sync so the CSS padding-top offset stays accurate
  const syncNavHeight = (): void => {
    document.documentElement.style.setProperty(
      '--nav-height',
      `${header.offsetHeight}px`
    );
  };
  syncNavHeight();
  window.addEventListener('resize', syncNavHeight, { passive: true });

  let ticking    = false;
  let lastY      = window.scrollY;
  let isHidden   = false;
  let isScrolled = false;

  const HIDE_AFTER  = 60; // px scrolled down before nav hides
  const SHOW_AFTER  = 5;  // px scrolled up before nav reappears
  const SCROLLED_ON = 80; // px for desktop is-scrolled (add)
  const SCROLLED_OFF = 30; // px for desktop is-scrolled (remove)

  const update = (): void => {
    const y     = window.scrollY;
    const delta = y - lastY;

    // ── Mobile: hide / show on scroll direction ───────────────────────────
    // .nav-hidden only has visual effect on mobile via CSS (position:fixed +
    // translateY). On desktop the class is added/removed but has no effect.
    if (y <= 0) {
      // Overscroll past the top — always show
      if (isHidden) {
        isHidden = false;
        header.classList.remove('nav-hidden');
      }
    } else if (delta > 0 && y > HIDE_AFTER && !header.querySelector('.main-navigation.is-open')) {
      // Scrolling down past threshold — hide (skip while the mobile nav
      // dropdown is open, so it doesn't slide away out from under the user)
      if (!isHidden) {
        isHidden = true;
        header.classList.add('nav-hidden');
      }
    } else if (delta < -SHOW_AFTER) {
      // Scrolling up — reveal
      if (isHidden) {
        isHidden = false;
        header.classList.remove('nav-hidden');
      }
    }

    // ── Desktop: is-scrolled padding shrink (hysteresis) ─────────────────
    const nextScrolled = isScrolled ? y > SCROLLED_OFF : y > SCROLLED_ON;
    if (nextScrolled !== isScrolled) {
      isScrolled = nextScrolled;
      header.classList.toggle('is-scrolled', isScrolled);
    }

    lastY = y;
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });

  update();
}

// ─── Mobile hamburger toggle ────────────────────────────────────────────────
//
// Toggles the .is-open class on .main-navigation, which CSS uses to show the
// dropdown <ul> (about/programs links, hidden by default under
// breakpoint-comp) and swap the menu/close icon pair. Closes on Escape, on
// clicking outside the nav, or after clicking a link (full page loads here,
// but resets state cleanly for back/forward-cache restores).

function initMobileNavToggle(): void {
  const toggle = document.querySelector<HTMLButtonElement>('.nav-toggle');
  const nav = document.querySelector<HTMLElement>('.main-navigation');
  if (!toggle || !nav) return;

  const close = (): void => {
    nav.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
  };

  toggle.addEventListener('click', () => {
    const isOpen = nav.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(isOpen));
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && nav.classList.contains('is-open')) {
      close();
      toggle.focus();
    }
  });

  document.addEventListener('click', e => {
    if (nav.classList.contains('is-open') && !nav.contains(e.target as Node)) {
      close();
    }
  });

  nav.querySelectorAll('#primary-menu a').forEach(link => {
    link.addEventListener('click', close);
  });
}
